import React, {
  useCallback, useState, useMemo, useRef, ReactElement, RefObject,
} from 'react';
import { useNavigate } from 'react-router';
import Card from '../../../components/card/card.component';
import Table from '../../../components/table/table.component';
import TableHead from '../../../components/table-head/table-head.component';
import TableBody from '../../../components/table-body/table-body.component';
import TableRow from '../../../components/table-row/table-row.component';
import TableCell from '../../../components/table-cell/table-cell.component';
import List from '../../../components/list/list.component';
import ListItem from '../../../components/list-item/list-item.component';
import ListItemImage from '../../../components/list-item-image/list-item-image.component';
import ListItemText from '../../../components/list-item-text/list-item-text.component';
import VisibleCell from '../../../components/visible-cell/visible-cell.component';
import Image from '../../../components/image/image.component';
import NoContentBlock from '../../../components/no-content-block/no-content-block.component';
import TableCellShort from '../../../components/table-cell-short/table-cell-short.component';
import SinglePageTab from '../../app/common/single-page-tab/single-page-tab.component';
import ContentListContainerOffset
  from '../../app/common/content-list-container-offset/content-list-container-offset.component';
import ScreenService from '../../../services/screen-service';
import useScroll from '../../../hooks/use-scroll';
import useScreenSmall from '../../../hooks/use-screen-small';
import { ROUTE_DEAL } from '../../../constants/system/routes';
import { CONTACT_TYPE_PERSON } from '../../../constants/entity/contact-types';
import { PAGE_LIST_DEFAULT } from '../../../constants/system/pages';
import AssetDefaultImage from '../../../static/images/asset-default-image.png';

const ContactAssociatedDeals: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    isLoading,
    contactData,
    contactDealList,
    contactDealsTotal,
    onLoadBatchData,
  } = props;

  const navigate: ReactRouterNavigate = useNavigate();
  const isSmallScreen: boolean = useScreenSmall();
  const containerRef: RefObject<HTMLDivElement> | null = useRef(null);
  const itemListRef: RefObject<HTMLDivElement> | null = useRef(null);

  const [page, setPage]: [number, Function] = useState(PAGE_LIST_DEFAULT);

  const contactId: number | null = (contactData && contactData.id) || null;
  const commonContactDealsTotal: number = (contactDealList && contactDealList.length) || 0;

  const scrollDeps: DepsItem[] = useMemo((): DepsItem[] => [
    contactDealsTotal, commonContactDealsTotal, contactId, isLoading, isLoading, page,
  ], [contactDealsTotal, commonContactDealsTotal, contactId, isLoading, isLoading, page]);

  const tableContactDealListFields: TableField[] = useMemo((): TableField[] => [
    {
      id: 1,
      name: 'photo',
      title: 'Photo',
      component: (dealItem: Deal): ReactElement => (
        <TableCellShort isTextCenter>
          <Image
            isCentered
            text={dealItem.name}
            link={dealItem.mainPhotoThumbnail || dealItem.mainPhoto || AssetDefaultImage}
          />
        </TableCellShort>
      ),
    },
    {
      id: 2,
      name: 'name',
      title: 'Name',
      component: (dealItem: Deal): ReactElement => (
        <TableCell isHeader>
          {dealItem.name}
        </TableCell>
      ),
    },
    {
      id: 3,
      name: 'contactTitle',
      title: 'Contact Title',
      component: (dealItem: Deal): ReactElement => (
        <TableCell>
          {dealItem.contactTitle}
        </TableCell>
      ),
    },
    {
      id: 4,
      name: 'accessLevel',
      title: 'Access Level',
      isVisibleByType: CONTACT_TYPE_PERSON,
      component: (dealItem: Deal): ReactElement => (
        <TableCell>
          {dealItem.dealAccessLevel && dealItem.dealAccessLevel.title}
        </TableCell>
      ),
    },
    {
      id: 5,
      name: 'amount',
      title: 'Total Capitalization',
      component: (dealItem: Deal): ReactElement => (
        <TableCell>
          {dealItem.amount}
        </TableCell>
      ),
    },
    {
      id: 6,
      name: 'receivedDate',
      title: 'Date Received',
      component: (dealItem: Deal): ReactElement => (
        <TableCell>
          {dealItem.receivedDate}
        </TableCell>
      ),
    },
  ], []);

  const onRowClick: Function = useCallback((dealId: number): void => {
    const withNoSelection: boolean = ScreenService.checkNoSelection();

    if (withNoSelection) {
      navigate({
        pathname: `${ROUTE_DEAL}/${dealId}`,
      });
    }
  }, []);

  const getOptions: Function = useCallback((pageOption?: number): RequestOptions => {
    let options: RequestOptions = {};

    options = contactId ? { ...options, contactId } : options;
    options = pageOption ? { ...options, page: pageOption } : options;

    return options;
  }, [contactId]);

  const loadBatchData: Function = useCallback((): void => {
    if (onLoadBatchData) {
      onLoadBatchData(getOptions(page + PAGE_LIST_DEFAULT));
    }

    setPage(page + PAGE_LIST_DEFAULT);
  }, [page, contactId]);

  const onScroll: MouseScrollHandlerFunction = useCallback((): void => {
    if (containerRef && containerRef.current) {
      ScreenService.infiniteScroll(
        containerRef.current.scrollTop,
        containerRef.current.clientHeight,
        itemListRef && itemListRef.current ? itemListRef.current.clientHeight : 0,
        (): void => {
          if (contactDealsTotal > commonContactDealsTotal && !isLoading) {
            loadBatchData();
          }
        },
        isSmallScreen,
      );
    }
  }, [
    containerRef, itemListRef, contactDealsTotal, page, contactId, commonContactDealsTotal, isLoading, isSmallScreen,
  ]);

  useScroll(onScroll, scrollDeps);

  return (
    <SinglePageTab withNoPadding withNoButtonBlock isFullHeight={isSmallScreen}>
      {
        !!contactData && contactDealList && !!contactDealList.length ? (
          <Card>
            <ContentListContainerOffset
              withTabs
              withNoControls
              isDefault={isSmallScreen}
              ref={containerRef}
              onScroll={!isSmallScreen ? onScroll : undefined}
            >
              <div ref={itemListRef}>
                {
                  !isSmallScreen ? (
                    <Table withStickyHeader>
                      <TableHead isSubHeader>
                        <TableRow>
                          {
                            tableContactDealListFields.map((tableField: TableField): ReactElement => (
                              <VisibleCell key={tableField.id} tableField={tableField} entityType={contactData.type}>
                                <TableCell>
                                  {tableField.title}
                                </TableCell>
                              </VisibleCell>
                            ))
                          }
                        </TableRow>
                      </TableHead>
                      <TableBody isPlain withZebraRows>
                        {
                          contactDealList.map((dealItem: Deal): ReactElement => (
                            <TableRow
                              isButton
                              key={dealItem.id}
                              onClick={(): void => {
                                onRowClick(dealItem.id);
                              }}
                            >
                              {
                                tableContactDealListFields.map((tableField: TableField): ReactElement | boolean => (
                                  <VisibleCell
                                    key={tableField.id}
                                    tableField={tableField}
                                    entityType={contactData.type}
                                  >
                                    {!!tableField.component && tableField.component(dealItem)}
                                  </VisibleCell>
                                ))
                              }
                            </TableRow>
                          ))
                        }
                      </TableBody>
                    </Table>
                  ) : (
                    <List>
                      {
                        contactDealList.map((dealItem: Deal): ReactElement => (
                          <ListItem
                            key={dealItem.id}
                            onClick={(): void => {
                              onRowClick(dealItem.id);
                            }}
                          >
                            <ListItemImage
                              text={dealItem.name}
                              link={dealItem.mainPhotoThumbnail || dealItem.mainPhoto || AssetDefaultImage}
                            />
                            <ListItemText isBlock>
                              {dealItem.name}
                            </ListItemText>
                            <ListItemText isBlock isBlockRight>
                              {dealItem.contactTitle}
                            </ListItemText>
                          </ListItem>
                        ))
                      }
                    </List>
                  )
                }
              </div>
            </ContentListContainerOffset>
          </Card>
        ) : (
          <NoContentBlock isExtended>
            No deals
          </NoContentBlock>
        )
      }
    </SinglePageTab>
  );
});

export default ContactAssociatedDeals;
